import config from 'appconfig';

interface FormValues {
  summary?: string[];
  details?: string;
  contactEmail?: string;
  isAuthenticated?: boolean;
  timezone?: string;
}

export const transformPayloadForChannelPlatform = (formValues: FormValues) => {
  return {
    email: formValues?.contactEmail ? [formValues.contactEmail] : [],
    cst_support_forms_data_fieldDescMap: [
      '{"inquiry_category":"Inquiry Category","email":"Email","raised_obo_by":"Raised on behalf of by","language":"language","description":"Description"}',
    ],
    cst_support_forms_data_formKey: ['ca-growth-pricing-billing-and-licensing'],
    cst_support_forms_data_timezone: formValues?.timezone ? [formValues.timezone] : [''],
    language: ['en'],
    inquiry_category: ['sales_support'],
    support_channel: ['chat'],
    authenticated: [formValues?.isAuthenticated ? 'Yes' : 'No'],
  };
};

interface ChatConnectionConfigResponse {
  // eslint-disable-next-line camelcase
  channelPlatform_submitRequest?: unknown;
  // eslint-disable-next-line camelcase
  channelPlatform_createContact?: unknown;
}

export const transformChatContactDetailsForSalesChannel = (
  chatConnectionConfigResponse: ChatConnectionConfigResponse | undefined,
) => {
  const submitRequest =
    chatConnectionConfigResponse?.channelPlatform_submitRequest ||
    chatConnectionConfigResponse?.channelPlatform_createContact;

  return {
    // eslint-disable-next-line camelcase
    channelPlatform_submitRequest: submitRequest,
  };
};

export const constructMetadata = (formValues: FormValues) => {
  return JSON.stringify({
    flowId: config.omniChannelFlowId,
    formContext: formValues || {},
  });
};

/**
 * Get a cookie value by name from document.cookie
 */
const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i += 1) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length, cookie.length);
    }
  }

  return null;
};

/**
 * Remove surrounding double quotes from a string
 */
const removeDoubleQuotes = (str: string): string => {
  if (str.startsWith('"') && str.endsWith('"')) {
    return str.slice(1, -1);
  }
  return str;
};

/**
 * Get the anonymous ID from the Segment analytics cookie
 * The cookie value is stored with surrounding double quotes, so we remove them
 */
export const getAnonymousId = (): string => {
  const anonymousId = getCookie('ajs_anonymous_id');
  return anonymousId ? removeDoubleQuotes(anonymousId) : '';
};

// --- Chat URL identifier (for getChatConnectionConfigForSalesChannel GQL) ---

interface ChatUrlIdentifierEntry {
  attributes: Record<string, string>;
}

/**
 * Parses a WAC URL and returns the relative path (without leading/trailing slashes).
 * Strips the /wac prefix if present.
 */
export const getRelativePathFromWacUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    let pathname = urlObj.pathname.replace(/\/+$/, '');

    if (pathname.startsWith('/wac/')) {
      pathname = pathname.slice('/wac'.length);
    }
    return pathname.replace(/^\/+/, '');
  } catch {
    const match = /\/\/[^/]+(\/[^#?]*)/.exec(url);
    if (match?.[1]) {
      let pathname = match[1].replace(/\/+$/, '');
      if (pathname.startsWith('/wac/')) {
        pathname = pathname.slice('/wac'.length);
      }
      return pathname.replace(/^\/+/, '');
    }
    return '';
  }
};

/**
 * URL path/pattern -> chat URL identifier (object for future use cases).
 * Exact relative paths (from getRelativePathFromWacUrl) are checked first; then entries are
 * iterated in order and the first pattern that pathname/currentUrl includes is used.
 */
export const PATH_TO_CHAT_URL_IDENTIFIER: Record<string, ChatUrlIdentifierEntry> = {
  'purchase/product/jira-core.ondemand': {
    attributes: { Tag_URL: 'url:jwm_quote_order_form_cart' },
  },
  'purchase/product/jira-servicedesk.ondemand': {
    attributes: { Tag_URL: 'url:jsm_quote_order_form_cart' },
  },
  'purchase/product/confluence.ondemand': {
    attributes: { Tag_URL: 'url:confluence_quote_order_form_cart' },
  },
  'purchase/product/jira-software.ondemand': {
    attributes: { Tag_URL: 'url:jsw_quote_order_form_cart' },
  },
};

/**
 * Returns attributes for the current URL to send to getChatConnectionConfigForSalesChannel GQL.
 * Exact path match (via getRelativePathFromWacUrl) is tried first; then PATH_TO_CHAT_URL_IDENTIFIER
 * is iterated in order and the first pattern that pathname or currentUrl includes is used.
 */
export const getChatUrlIdentifier = (): Record<string, string> => {
  const currentUrl = window.location.href;
  const relativePath = getRelativePathFromWacUrl(currentUrl);

  const exact = PATH_TO_CHAT_URL_IDENTIFIER[relativePath];
  if (exact) {
    return exact.attributes;
  }

  return { Tag_URL: 'url:no_tag_applicable' };
};
